import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, MessageSquare } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { formatRelative } from "@/lib/format";
import type { Room } from "@/lib/types";

interface NotificationDialogProps {
  open: boolean;
  onClose: () => void;
  rooms: Room[];
  loading: boolean;
  error: Error | null;
  onRefresh: () => void;
}

export function NotificationDialog({ open, onClose, rooms, loading, error, onRefresh }: NotificationDialogProps) {
  const { t, i18n } = useTranslation();
  const unreadRooms = rooms.filter(room => room.unreadCount > 0)
    .sort((a, b) => (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? ""));

  return createPortal(
    <Modal open={open} onClose={onClose} title={t("notificationInbox.title")} size="sm">
      <div className="mb-4 flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onRefresh} disabled={loading}>{t("notificationInbox.refresh")}</Button>
        <Button size="sm" variant="secondary" onClick={onClose}>{t("common.close")}</Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">{t("notificationInbox.error")} {error.message}</p>
      ) : loading ? (
        <p role="status" className="py-4 text-center text-sm text-fg-muted">{t("notificationInbox.loading")}</p>
      ) : unreadRooms.length === 0 ? (
        <div role="status" className="py-6 text-center">
          <Bell size={24} aria-hidden className="mx-auto mb-3 text-fg-muted" />
          <p className="text-sm text-fg-secondary">{t("notificationInbox.empty")}</p>
        </div>
      ) : (
        <ul className="space-y-1">
          {unreadRooms.map(room => (
            <li key={room.id}>
              <Link
                to={`/rooms/${encodeURIComponent(room.id)}`}
                onClick={onClose}
                className="flex min-h-11 items-center gap-3 rounded-md px-3 py-3 hover:bg-surface-base focus-visible:shadow-focus"
              >
                <MessageSquare size={18} aria-hidden className="shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm font-medium text-fg-primary">{room.name}</span>
                  {room.lastMessageAt && <time dateTime={room.lastMessageAt} className="mt-1 block text-xs text-fg-muted">{formatRelative(room.lastMessageAt, i18n.resolvedLanguage ?? i18n.language)}</time>}
                </span>
                <Badge tone="brand" className="shrink-0"><span aria-hidden>{room.unreadCount}</span><span className="sr-only">{t("notificationInbox.unreadCount", { count: room.unreadCount })}</span></Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Modal>,
    document.body,
  );
}
